import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { settingsInvoicesApi, SubscriptionInvoice } from '../api/invoices-api';

interface InvoicesState {
  invoices: SubscriptionInvoice[];
  currentInvoice: SubscriptionInvoice | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    status?: string;
  };
}

const initialState: InvoicesState = {
  invoices: [],
  currentInvoice: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchInvoices = createAsyncThunk(
  'settingsInvoices/fetchInvoices',
  async (
    filters: { status?: string; page?: number; limit?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await settingsInvoicesApi.getInvoices(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar facturas'
      );
    }
  }
);

export const fetchInvoice = createAsyncThunk(
  'settingsInvoices/fetchInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const invoice = await settingsInvoicesApi.getInvoiceById(id);
      return invoice;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar factura'
      );
    }
  }
);

// Slice
const invoicesSlice = createSlice({
  name: 'settingsInvoices',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<{ status?: string }>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentInvoice: (state, action: PayloadAction<SubscriptionInvoice | null>) => {
      state.currentInvoice = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: (builder) => {
    // Fetch invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        if (state.invoices.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices || [];
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 0,
        };
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch invoice
    builder
      .addCase(fetchInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(fetchInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, setCurrentInvoice, clearError, setPagination } =
  invoicesSlice.actions;

export default invoicesSlice.reducer;

